import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
} from "react";

import "./CheckboxChip.scss";

type CheckboxChipProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "type"
> & {
  label: string;
  icon?: ReactNode;
  error?: string;
  hint?: string;
};

export const CheckboxChip = forwardRef<
  HTMLInputElement,
  CheckboxChipProps
>(
  (
    {
      id,
      name,
      label,
      icon,
      error,
      hint,
      className = "",
      disabled,
      checked,
      defaultChecked,
      onChange,
      ...inputProps
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = id ?? `${name ?? "checkbox"}-${generatedId}`;
    const errorId = `${inputId}-error`;
    const hintId = `${inputId}-hint`;

    const describedBy = [
      error ? errorId : undefined,
      !error && hint ? hintId : undefined,
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <div
        className={[
          "checkbox-chip",
          error ? "checkbox-chip--error" : "",
          disabled ? "checkbox-chip--disabled" : "",
          className,
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <label
          className="checkbox-chip__label"
          htmlFor={inputId}
        >
          <input
            {...inputProps}
            ref={ref}
            id={inputId}
            name={name}
            type="checkbox"
            checked={checked}
            defaultChecked={defaultChecked}
            disabled={disabled}
            onChange={onChange}
            aria-invalid={Boolean(error)}
            aria-describedby={describedBy || undefined}
            className="checkbox-chip__input"
          />

          <span
            className="checkbox-chip__control"
            aria-hidden="true"
          >
            <span className="checkbox-chip__check">
              ✓
            </span>
          </span>

          {icon && (
            <span
              className="checkbox-chip__icon"
              aria-hidden="true"
            >
              {icon}
            </span>
          )}

          <span className="checkbox-chip__text">
            {label}
          </span>
        </label>

        {error ? (
          <p
            id={errorId}
            className="checkbox-chip__message checkbox-chip__message--error"
            role="alert"
          >
            {error}
          </p>
        ) : hint ? (
          <p
            id={hintId}
            className="checkbox-chip__message"
          >
            {hint}
          </p>
        ) : null}
      </div>
    );
  }
);

CheckboxChip.displayName = "CheckboxChip";